import React, { useState } from 'react';
import { MapPin, Clock, Calendar, User, Users as UsersIcon, Navigation, ChevronRight, Heart, Lock, Mail, X, ZoomIn } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function ClubCard({ club, onJoin, onLeave, isMember, userLocation, hideJoin = true}) {
  const { isAuthenticated, user } = useAuth();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const handleAction = () => {
    if (isMember) {
      onLeave(club._id);
    } else {
      onJoin(club._id);
    }
  };

  // Fonction pour ouvrir le modal d'image
  const handleImageClick = (e) => {
    e.preventDefault();
    if (club.imageClub) {
      setShowImageModal(true);
    }
  };

  // Vérifier si l'utilisateur est invité
  const isInvited = club.invitations?.some(
    inv => inv.user === user?._id && inv.status === 'pending'
  );

  // Vérifier si c'est un club privé
  const isPrivate = club.isPrivate;

  // Déterminer si l'utilisateur peut rejoindre
  const canJoin = !isPrivate || isInvited;

  return (
    <>
      <div className={`bg-slate-800 border rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 ${
        isPrivate 
          ? 'border-indigo-500/50 hover:border-indigo-500' 
          : 'border-slate-700 hover:border-indigo-500 hover:shadow-indigo-500/10'
      }`}>
        <div className="md:flex">
          {/* Image du club */}
          <div className="md:w-1/3 h-40 sm:h-48 md:h-auto relative group">
            {club.imageClub ? (
              <div 
                onClick={handleImageClick}
                className="w-full h-full cursor-zoom-in relative overflow-hidden"
              >
                <img
                  src={club.imageClub}
                  alt={club.nom}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {/* Overlay zoom au hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/20 backdrop-blur-sm rounded-full p-3">
                    <ZoomIn size={24} className="text-white" />
                  </div>
                </div>
              </div>
            ) : (
              <Link to={`/club/${club._id}`}>
                <div className={`w-full h-full bg-gradient-to-br flex items-center justify-center hover:opacity-90 transition-all ${
                  isPrivate 
                    ? 'from-indigo-900/20 to-slate-900' 
                    : 'from-slate-700 to-slate-900'
                }`}>
                  {isPrivate ? (
                    <Lock size={48} className="text-indigo-500/50 sm:w-16 sm:h-16" />
                  ) : (
                    <UsersIcon size={48} className="text-slate-600 sm:w-16 sm:h-16" />
                  )}
                </div>
              </Link>
            )}
            
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-40 pointer-events-none"></div>
            
            {/* Badges */}
            <div className="absolute top-2 sm:top-3 right-2 sm:right-3 flex flex-col gap-2">
              {/* Badge Privé */}
              {isPrivate && (
                <span className="bg-indigo-500/90 backdrop-blur-sm text-white py-1  sm:py-1 rounded-full text-xs sm:text-sm font-bold shadow-lg flex items-center gap-1 justify-center">
                  <Lock size={12} />
                  <span>Privé</span>
                </span>
              )}
              
              {/* Badge Niveau */}
              <span className="bg-slate-900/90 backdrop-blur-sm border border-slate-700 text-white px-2.5 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg ">
                {club.niveau}
              </span>
              
              {/* Badge Membre */}
              {isMember && (
                <span className="bg-indigo-500/90 backdrop-blur-sm text-white px-2.5 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg flex items-center justify-center gap-1">
                  <Heart size={12} className="fill-current" />
                  <span className="hidden sm:inline">Membre</span>
                </span>
              )}
              
              {/* Badge Invité */}
              {isInvited && !isMember && (
                <span className="bg-green-500/90 backdrop-blur-sm text-white px-2.5 py-1 sm:px-3 sm:py-1 rounded-full text-xs sm:text-sm font-semibold shadow-lg flex items-center justify-center gap-1">
                  <Mail size={12} />
                  <span className="hidden sm:inline">Invité</span>
                </span>
              )}
            </div>

            {/* Badge de distance */}
            {club.distanceFromUser !== undefined && club.distanceFromUser !== null && (
              <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3">
                <span className="bg-indigo-500/90 backdrop-blur-sm text-white px-2.5 py-1.5 sm:px-3 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold shadow-lg flex items-center gap-1">
                  <Navigation size={12} className="sm:w-3.5 sm:h-3.5" />
                  {club.distanceFromUser < 1 
                    ? `${Math.round(club.distanceFromUser * 1000)}m`
                    : `${club.distanceFromUser}km`
                  }
                </span>
              </div>
            )}
          </div>

          {/* Contenu du club */}
          <div className="md:w-2/3 p-4 sm:p-6 flex flex-col justify-between">
            <div>
              {/* Titre */}
              <Link to={`/club/${club._id}`}>
                <div className="flex items-center justify-between mb-2 group">
                  <h3 className={`text-xl sm:text-2xl font-bold transition-colors ${
                    isPrivate 
                      ? 'text-white group-hover:text-indigo-500' 
                      : 'text-white group-hover:text-indigo-500'
                  }`}>
                    {club.nom}
                  </h3>
                  <ChevronRight 
                    size={20} 
                    className={`transition-all flex-shrink-0 ml-2 ${
                      isPrivate
                        ? 'text-gray-400 group-hover:text-indigo-500 group-hover:translate-x-1'
                        : 'text-gray-400 group-hover:text-indigo-500 group-hover:translate-x-1'
                    }`}
                  />
                </div>
              </Link>

              {/* Description */}
              <div className="mb-3 sm:mb-4">
                <p className={`text-sm sm:text-base text-gray-400 ${
                  isExpanded ? '' : 'line-clamp-2'
                }`}>
                  {club.description}
                </p>
                {club.description && club.description.length > 100 && (
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`text-xs sm:text-sm font-medium mt-1 md:hidden ${
                      isPrivate 
                        ? 'text-indigo-500 hover:text-indigo-400' 
                        : 'text-indigo-500 hover:text-indigo-400'
                    }`}
                  >
                    {isExpanded ? 'Voir moins' : 'Voir plus'}
                  </button>
                )}
              </div>

              {/* Grid d'infos */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4">
                <div className={`flex items-center text-gray-300 bg-slate-700/50 border rounded-lg p-2 sm:p-2.5 hover:bg-slate-700 transition ${
                  isPrivate 
                    ? 'border-indigo-500/30 hover:border-indigo-500/50' 
                    : 'border-slate-700 hover:border-indigo-500/50'
                }`}>
                  <MapPin size={16} className={`mr-1.5 sm:mr-2 flex-shrink-0 sm:w-[18px] sm:h-[18px] ${
                    isPrivate ? 'text-indigo-500' : 'text-indigo-500'
                  }`} />
                  <span className="text-xs sm:text-sm font-medium truncate">{club.ville}</span>
                </div>
                {/*
                <div className={`flex items-center text-gray-300 bg-slate-700/50 border rounded-lg p-2 sm:p-2.5 hover:bg-slate-700 transition ${
                  isPrivate 
                    ? 'border-indigo-500/30 hover:border-indigo-500/50' 
                    : 'border-slate-700 hover:border-indigo-500/50'
                }`}>
                  <Clock size={16} className={`mr-1.5 sm:mr-2 flex-shrink-0 sm:w-[18px] sm:h-[18px] ${
                    isPrivate ? 'text-indigo-500' : 'text-indigo-500'
                  }`} />
                  <span className="text-xs sm:text-sm font-medium truncate">{club.distance}</span>
                </div>
                
                <div className={`flex items-center text-gray-300 bg-slate-700/50 border rounded-lg p-2 sm:p-2.5 hover:bg-slate-700 transition ${
                  isPrivate 
                    ? 'border-indigo-500/30 hover:border-indigo-500/50' 
                    : 'border-slate-700 hover:border-indigo-500/50'
                }`}>
                  <Calendar size={16} className={`mr-1.5 sm:mr-2 flex-shrink-0 sm:w-[18px] sm:h-[18px] ${
                    isPrivate ? 'text-indigo-500' : 'text-indigo-500'
                  }`} />
                  <span className="text-xs sm:text-sm font-medium truncate">
                    {club.nombreSeances || club.seances?.length || 0} séance{(club.nombreSeances || club.seances?.length || 0) > 1 ? 's' : ''}/sem
                  </span>
                </div>
                */}
                <div className={`flex items-center text-gray-300 bg-slate-700/50 border rounded-lg p-2 sm:p-2.5 hover:bg-slate-700 transition ${
                  isPrivate 
                    ? 'border-indigo-500/30 hover:border-indigo-500/50' 
                    : 'border-slate-700 hover:border-indigo-500/50'
                }`}>
                  <User size={16} className={`mr-1.5 sm:mr-2 flex-shrink-0 sm:w-[18px] sm:h-[18px] ${
                    isPrivate ? 'text-indigo-500' : 'text-indigo-500'
                  }`} />
                  <span className="text-xs sm:text-sm font-medium">
                    {club.nombreMembres || club.membres?.length || 0} membre{(club.nombreMembres || club.membres?.length || 0) > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Boutons d'action */}
            {hideJoin && (
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-2">
                {isAuthenticated && (
                  <>
                    {/*{isMember ? (
                      <button
                        onClick={handleAction}
                        className="w-full sm:flex-1 py-2.5 sm:py-2 rounded-lg transition-all font-semibold text-sm sm:text-base flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white border border-red-500 active:scale-95"
                      >
                        <span>Quitter le club</span>
                      </button>
                    ) : canJoin ? (
                      <button
                        onClick={handleAction}
                        className={`w-full sm:flex-1 py-2.5 sm:py-2 rounded-lg transition-all font-semibold text-sm sm:text-base flex items-center justify-center gap-2 active:scale-95 shadow-lg ${
                          isPrivate
                            ? 'bg-indigo-500 hover:bg-indigo-600 text-slate-900 border border-indigo-400 shadow-indigo-500/20'
                            : 'bg-indigo-500 hover:bg-indigo-600 text-white border border-indigo-400 shadow-indigo-500/20'
                        }`}
                      >
                        <UsersIcon size={18} />
                        <span>{isInvited ? 'Accepter l\'invitation' : 'Rejoindre'}</span>
                      </button>
                    ) : (
                      <div className="w-full sm:flex-1 py-2.5 sm:py-2 rounded-lg bg-slate-700/50 border border-indigo-500/50 text-indigo-500 font-semibold text-xs sm:text-sm flex items-center justify-center gap-2">
                        <Lock size={16} />
                        <span>Groupe privé - Invitation requise</span>
                      </div>
                    )}*/}
                  </>
                )}
                
                <Link
                  to={`/club/${club._id}`}
                  className={`${
                    isAuthenticated ? 'sm:flex-1' : 'w-full'
                  } bg-slate-700 hover:bg-slate-600 text-white border hover:border-indigo-500 py-2.5 sm:py-2 rounded-lg active:scale-95 transition-all font-semibold text-sm sm:text-base text-center flex items-center justify-center gap-2 ${
                    isPrivate ? 'border-indigo-500/50' : 'border-slate-600'
                  }`}
                >
                  <span>Voir les détails</span>
                  <ChevronRight size={18} />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Indicateur hover subtil */}
        <div className={`h-px bg-gradient-to-r from-transparent to-transparent opacity-0 group-hover:opacity-50 transition-opacity ${
          isPrivate ? 'via-indigo-500' : 'via-indigo-500'
        }`}></div>
      </div>

      {/* Modal de l'image */}
      {showImageModal && club.imageClub && (
        <div 
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 animate-fadeIn"
          onClick={() => setShowImageModal(false)}
        >
          {/* Bouton fermer */}
          <button
            onClick={() => setShowImageModal(false)}
            className="absolute top-4 right-4 z-[10000] bg-white/10 hover:bg-white/20 backdrop-blur-md text-white p-3 rounded-full transition-all hover:scale-110 active:scale-95 shadow-2xl border border-white/20"
            aria-label="Fermer"
          >
            <X size={24} />
          </button>

          {/* Nom du club en haut */}
          <div className="absolute top-4 left-4 right-20 z-[10000]">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-4 py-3 shadow-2xl">
              <h3 className="text-white font-bold text-lg sm:text-xl truncate">{club.nom}</h3>
              <p className="text-gray-300 text-sm">{club.ville}</p>
            </div>
          </div>

          {/* Image */}
          <div 
            className="relative max-w-7xl max-h-[85vh] w-full h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={club.imageClub}
              alt={club.nom}
              className="max-w-full max-h-full object-contain rounded-2xl shadow-2xl animate-scaleIn"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Instructions en bas */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[10000]">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-2 shadow-2xl">
              <p className="text-white text-sm font-medium">Cliquez n'importe où pour fermer</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ClubCard;